import { seoThumbUrl, slugFromSrc, seoAvatar, seoWide, type SeoImageInput } from '@/lib/imageSeo';

const CDN = 'https://expert-reviews.global.ssl.fastly.net';

// Percent-encodes each path segment while leaving '/' (and an existing '?query') intact.
// Root-caused 2026-07-29: pro/article image paths are stored in the DB as raw, unencoded
// Hebrew (e.g. /images/יועצי-מס/מלכיאל-1643.jpg — see lib/proImageSeo.ts). A plain <img
// src> with raw Hebrew works fine (browsers percent-encode it themselves on fetch), but
// the FIRST/LCP image on a page gets fetchPriority="high" (CategoryProCard.tsx,
// ArticleView.tsx) — which makes Next.js auto-emit an HTTP `Link: <...>; rel=preload`
// resource-hint header built from the raw src string. That header assembly runs through
// undici's low-level Headers/Request internals, which throw a hard, uncaught
// "Cannot convert argument to a ByteString" TypeError for any character above 0xFF —
// crashing the ENTIRE request, not just the image. Confirmed via live production capture:
// this was the actual cause of a long-running, hard-to-reproduce site-wide crash
// (see [[experts_coil_bytestring_crash_2026-07-29]]). encodeURI-ing each already-decoded
// path segment (not the whole string — that would also encode the '/' separators) makes
// the src safe for both the <img> tag AND any header Next builds from it.
export function encodeImagePath(path: string): string {
  const [base, query] = path.split('?');
  const encodedBase = base.split('/').map(seg => encodeURIComponent(seg)).join('/');
  return query ? `${encodedBase}?${query}` : encodedBase;
}

export function proImgUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith('/')) return encodeImagePath(path);
  if (path.startsWith('http')) return path;
  return `${CDN}/${encodeImagePath(path)}`;
}

/** Square/wide WebP derivative of a stored image. IMAGE-SEO LAW (14/10/26): the URL carries a keyword filename — `slug` when the caller knows the
 *  context (lib/imageSeo.ts seoImage), else the one the source path already tells (/images/<קטגוריה>/<שם>-<id> → /pic/<size>/<b64>/<קטגוריה-שם-id>.webp);
 *  a source with no readable name (hash-named /api/img/gallery/…) keeps the legacy /api/img/thumb URL until a caller passes context. */
export function proThumbUrl(src: string | null | undefined, size: ThumbSize = 72, slug?: string): string | null {
  if (!src) return null;
  if (!src.startsWith('/images/') && !src.startsWith('/api/img/') && !src.startsWith('/img/pricing/') && !src.startsWith('/img/topics/')) return src;   // generated avatars / external URLs stay as they are
  const sl = slug || slugFromSrc(src);
  if (sl) return seoThumbUrl(src, size, sl);
  const b64 = typeof window === 'undefined' ? Buffer.from(src, 'utf8').toString('base64url')
    : btoa(unescape(encodeURIComponent(src))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `/api/img/thumb/${size}/${b64}.webp`;
}

// IMAGE OPTIMIZATION LAW (Founder 2026-09-29: "a 1024px photo for a 94px avatar — who needs that?"): a public <img> never loads a
// stored original. Squares (faces, icons) → avatarImg(src, cssPx); wide photos (article heroes, work galleries) → wideImg(src, sizes).
// Both return { src, srcSet } of WebP derivatives from /api/img/thumb — the smallest cached size that covers 1x/2x/3x screens.
export type ThumbSize = 48 | 72 | 96 | 144 | 192 | 288 | 384 | 576 | 864 | 'w320' | 'w480' | 'w640' | 'w960' | 'w1280';
const SQ = [48, 72, 96, 144, 192, 288, 384, 576, 864] as const;
const WIDE = [320, 480, 640, 960, 1280] as const;
const pickSq = (px: number) => SQ.find(s => s >= px) ?? 864;

/** Square avatar sized to how it is SHOWN (css px): src = 1x, srcSet 1x/2x/3x. External/generated avatars pass through.
 *  Pass `seo` ({ kind, ctx }) and the result also carries keyword `alt` + `title` and a keyword-named URL (lib/imageSeo.ts). */
export function avatarImg(src: string | null | undefined, cssPx: number, seo?: Omit<SeoImageInput, 'src' | 'size'>): { src: string; srcSet?: string; alt?: string; title?: string } {
  if (seo) return seoAvatar(src, cssPx, seo);
  const one = proThumbUrl(src, pickSq(cssPx));
  if (!src || !one || one === src) return { src: src || '' };
  return { src: one, srcSet: [1, 2, 3].map(d => `${proThumbUrl(src, pickSq(cssPx * d))} ${d}x`).join(', ') };
}

/** Wide photo (no crop) as a width-descriptor srcSet; `sizes` = the CSS slot, e.g. "(min-width: 768px) 300px, 80vw". `seo` as in avatarImg. */
export function wideImg(src: string | null | undefined, sizes: string, fallbackW: 320 | 480 | 640 | 960 = 640, seo?: Omit<SeoImageInput, 'src' | 'size'>): { src: string; srcSet?: string; sizes?: string; alt?: string; title?: string } {
  if (seo) return seoWide(src, sizes, seo, fallbackW);
  const one = proThumbUrl(src, `w${fallbackW}`);
  if (!src || !one || one === src) return { src: src || '' };
  return { src: one, srcSet: WIDE.map(w => `${proThumbUrl(src, `w${w}` as ThumbSize)} ${w}w`).join(', '), sizes };
}
